import type { Locale } from '../platform.js';
import { en, type EnMessages } from './locales/en.js';
import { ru } from './locales/ru.js';

export type MessageNamespace = keyof EnMessages;
export type Messages = EnMessages;

/** Fully qualified message id, e.g. `catalog.title`. */
export type MessageId = {
  [NS in MessageNamespace]: `${NS}.${keyof EnMessages[NS] & string}`;
}[MessageNamespace];

export const catalogs: Record<Locale, Messages> = { en, ru };

type Leaves<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

export { en, ru };
export type { EnMessages };
export type NamespaceMessages<NS extends MessageNamespace> = EnMessages[NS];
export type MessageKey<NS extends MessageNamespace> = Leaves<EnMessages[NS]> & string;

export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(
  locale: Locale,
  id: MessageId,
  params?: Record<string, string | number>,
): string {
  const [namespace, key] = id.split('.') as [MessageNamespace, string];
  const bucket = (catalogs[locale] ?? en)[namespace] as Record<string, string>;
  const template = bucket[key] ?? (en[namespace] as Record<string, string>)[key] ?? id;
  return interpolate(template, params);
}

export function createTranslator(locale: Locale) {
  return (id: MessageId, params?: Record<string, string | number>): string =>
    translate(locale, id, params);
}

export type Translator = ReturnType<typeof createTranslator>;

export function detectLocale(acceptLanguage: string | null | undefined): Locale {
  if (!acceptLanguage) return 'en';
  const tags = acceptLanguage
    .split(',')
    .map((part) => part.trim().split(';')[0]?.toLowerCase() ?? '')
    .filter(Boolean);
  for (const tag of tags) {
    if (tag === 'ru' || tag.startsWith('ru-')) return 'ru';
    if (tag === 'en' || tag.startsWith('en-')) return 'en';
  }
  return 'en';
}
